import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Download, History, KeyRound, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import { DateField } from '@/components/dateFields';
import { appDateTime } from '@/utils/appDateFormat';
import { todayLocalDateParam } from '@/utils/apiDate';
import { isGuid, maskGuids, withoutGuid } from '@/utils/systemIds';
import { PageLayout } from '../../../../components/ui/PageLayout';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../../components/ui/Table';
import Button from '../../../../components/ui/Button';
import Select from '../../../../components/ui/Select';
import Input from '../../../../components/ui/Input';
import { StatusBadge } from '../../../../components/ui/StatusBadge';
import { Skeleton } from '../../../../components/ui/Skeleton';
import Pagination from '../../../../components/Pagination';
import {
  ACTION_TRAIL_KIND_CODES,
  ACTION_TRAIL_OUTCOME_CODES,
  ActionTrailArea,
  ActionTrailChange,
  ActionTrailEvent,
  ActionTrailFieldChange,
  ActionTrailKind,
  ActionTrailOutcome,
  ActionTrailPersonKind,
  ActionTrailQuery,
  exportActionTrailPage,
  getActionTrailAreas,
  getActionTrailEvents,
} from '../../../../api/General/ActionTrail';

/**
 * CO-830 — the Action Trail (Admin and HR): every change made in the company, by whom, when, and what
 * changed (before/after, owner 2026-10-06).
 *
 * Scope and redaction are decided by the SERVER, never here (design doc §6). HR gets every row unredacted.
 * For an Admin reading an EmployeeData row the server drops the subject (person, record id, concrete path)
 * and the changes before they leave the API, and blanks personal/financial values on every row, so the page
 * only explains the gap; there is no client toggle that could reveal it. The page does not branch on role.
 *
 * Design (ui-ux-pro-max + taste-skill, then impeccable polish, product register), modelled on the Security
 * Audit Log: restrained and dense. Filters sit inline above the table and wrap on narrow screens; details
 * open inline under the row (no modal) and carry the whole action: its description, a before/after table per
 * record it wrote, and every recorded parameter. Numbers are tabular; logical properties only.
 */

const KINDS = Object.keys(ACTION_TRAIL_KIND_CODES) as ActionTrailKind[];
const OUTCOMES = Object.keys(ACTION_TRAIL_OUTCOME_CODES) as ActionTrailOutcome[];
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const COLUMN_COUNT = 6;
/** Kinds worth a chip. "Other" is the default bucket and says nothing, so it gets no chip. */
const CHIP_KINDS = new Set<string>(['Create', 'Update', 'Delete']);

interface Filters {
  from: string;
  to: string;
  area: string;
  kind: string;
  outcome: string;
  actor: string;
  targetUser: string;
}

const EMPTY_FILTERS: Filters = { from: '', to: '', area: '', kind: '', outcome: '', actor: '', targetUser: '' };

/**
 * PascalCase identifier → sentence ("UpdateMatch" → "Update match", "ExportHRReport" → "Export HR report").
 * Acronyms stay upper case. v1 action labels are derived this way (owner decision §9.6).
 */
function humanise(name: string): string {
  if (!name) return '';
  const words = name
    .replace(/Async$/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim()
    .split(/\s+/);
  return words
    .map((w, i) => {
      if (w.length > 1 && w === w.toUpperCase()) return w;
      const lower = w.toLowerCase();
      return i === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    })
    .join(' ');
}

const hasAnyFilter = (f: Filters) => Object.values(f).some((v) => v.trim() !== '');

/** "2026-10-06T06:21:27.123Z" → "2026-10-06 06:21:27 UTC": the stored instant, for cross-checking logs. */
function utcStamp(utc: string | null | undefined): string | null {
  if (!utc) return null;
  const d = new Date(utc);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.toISOString().slice(0, 19).replace('T', ' ')} UTC`;
}

const ActionTrail: React.FC = () => {
  const { t } = useTranslation();

  const [draft, setDraft] = useState<Filters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<Filters>(EMPTY_FILTERS);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [rows, setRows] = useState<ActionTrailEvent[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [areas, setAreas] = useState<ActionTrailArea[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  // The table scrolls sideways on narrow screens, and the details row spans the whole (wider) table. Its panel
  // is capped to the card's visible width and pinned to the inline start, so it wraps instead of being clipped.
  const cardRef = useRef<HTMLDivElement>(null);
  const [panelMaxWidth, setPanelMaxWidth] = useState<number | undefined>(undefined);
  useEffect(() => {
    const el = cardRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setPanelMaxWidth(Math.max(0, el.clientWidth - 48)));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const query: ActionTrailQuery = useMemo(() => ({
    from: applied.from || undefined,
    to: applied.to || undefined,
    area: applied.area || undefined,
    kind: applied.kind !== '' ? ACTION_TRAIL_KIND_CODES[applied.kind as ActionTrailKind] : undefined,
    outcome: applied.outcome !== '' ? ACTION_TRAIL_OUTCOME_CODES[applied.outcome as ActionTrailOutcome] : undefined,
    actor: applied.actor.trim() || undefined,
    targetUser: applied.targetUser.trim() || undefined,
    pageNumber,
    pageSize,
  }), [applied, pageNumber, pageSize]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const result = await getActionTrailEvents(query);
      if (result.errors?.length || !result.payload) {
        setLoadError(true);
        setRows([]);
        return;
      }
      setRows(result.payload.items ?? []);
      setTotalCount(result.payload.totalCount ?? 0);
      setTotalPages(result.payload.totalPages ?? 0);
    } catch {
      setLoadError(true);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    getActionTrailAreas()
      .then((r) => { if (!r.errors?.length && r.payload) setAreas(r.payload); })
      .catch(() => undefined);
  }, []);

  const areaLabel = (area: string) => t(`actionTrail.area.${area}`, { defaultValue: humanise(area) });
  const kindLabel = (kind: string) => t(`actionTrail.kind.${kind}`, { defaultValue: kind });
  const outcomeLabel = (outcome: string) => t(`actionTrail.outcome.${outcome}`, { defaultValue: outcome });

  const formatWhen = (utc: string) => {
    const d = new Date(utc);
    if (Number.isNaN(d.getTime())) return utc;
    return appDateTime(d, true);
  };

  const outcomeBadge = (outcome: string) => {
    const variant = outcome === 'Success' ? 'success' : outcome === 'Denied' ? 'warning' : 'error';
    return <StatusBadge variant={variant} size="sm">{outcomeLabel(outcome)}</StatusBadge>;
  };

  // A quiet outline chip, not a coloured pill: the outcome column already carries the colour, and two
  // coloured badges per row turn a dense log into noise. Delete alone is tinted, since it is the one
  // kind an Admin scans for.
  const kindChip = (kind: string) => CHIP_KINDS.has(kind) && (
    <span
      className={
        'inline-flex shrink-0 items-center rounded border px-1.5 py-px text-[11px] font-medium leading-4 ' +
        (kind === 'Delete'
          ? 'border-red-200 text-red-700 dark:border-red-900/60 dark:text-red-300'
          : 'border-border text-muted-foreground')
      }
    >
      {kindLabel(kind)}
    </span>
  );

  const applyFilters = () => {
    setPageNumber(1);
    setExpandedId(null);
    setApplied({ ...draft });
  };

  const resetFilters = () => {
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setPageNumber(1);
    setExpandedId(null);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportActionTrailPage(query);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `action-trail-${todayLocalDateParam()}-p${pageNumber}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('actionTrail.exportFailed'));
    } finally {
      setExporting(false);
    }
  };

  const areaOptions = [
    { value: '', label: t('actionTrail.allAreas') },
    ...[...areas]
      .map((a) => ({ value: a.area, label: areaLabel(a.area) }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];
  const kindOptions = [
    { value: '', label: t('actionTrail.allKinds') },
    ...KINDS.map((k) => ({ value: k, label: kindLabel(k) })),
  ];
  const outcomeOptions = [
    { value: '', label: t('actionTrail.allOutcomes') },
    ...OUTCOMES.map((o) => ({ value: o, label: outcomeLabel(o) })),
  ];

  // A person is their name, else their staff no., else "Mowazaf support" for a platform user or "Former
  // employee": never the internal user id, which is a system key (owner rule 2026-10-28) and no longer reaches
  // the client (CO-953). Null when there is no person at all. An API older than `actorKind` (rolling deploy)
  // sends no kind: a resolved name or staff no. still shows rather than every row reading "System".
  const personLabel = (kind?: ActionTrailPersonKind, name?: string | null, staffNo?: string | null) => {
    if (kind === 'System') return null;
    if (name || staffNo) return name || staffNo;
    if (!kind) return null;
    return kind === 'Support' ? t('people.mowazafSupport') : t('people.formerEmployee');
  };
  const actorLabel = (row: ActionTrailEvent) =>
    personLabel(row.actorKind, row.actorName, row.actorStaffNo) ?? t('actionTrail.system');
  const recordText = (row: ActionTrailEvent) => [row.targetType, withoutGuid(row.targetId)].filter(Boolean).join(' · ');

  const actorCell = (row: ActionTrailEvent) => {
    const label = personLabel(row.actorKind, row.actorName, row.actorStaffNo);
    if (!label) return <span className="text-muted-foreground">{t('actionTrail.system')}</span>;
    return (
      <span className="block max-w-[12rem] truncate" title={label ?? undefined}>
        {label}
      </span>
    );
  };

  const subjectCell = (row: ActionTrailEvent) => {
    if (row.redacted) {
      return (
        <span
          className="inline-flex max-w-[12rem] items-center gap-1.5 text-muted-foreground"
          title={`${t('actionTrail.redacted')}. ${t('actionTrail.redactedHint')}`}
          data-testid="action-trail-redacted"
        >
          <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="truncate">{t('actionTrail.redacted')}</span>
        </span>
      );
    }
    const record = recordText(row);
    const subject = personLabel(row.targetKind, row.targetName, row.targetStaffNo);
    if (subject) {
      return <span className="block max-w-[12rem] truncate" title={record || undefined}>{subject}</span>;
    }
    // Platform-terms acceptances carry the terms version as their record id; say so in words. The raw
    // record stays in the row details.
    if (row.targetType === 'PlatformTerms') {
      const label = row.targetId
        ? t('actionTrail.subjectPlatformTerms', { version: row.targetId })
        : t('actionTrail.subjectPlatformTermsNoVersion');
      return <span className="block max-w-[12rem] truncate" title={label}>{label}</span>;
    }
    return record
      ? <span className="block max-w-[12rem] truncate font-mono text-xs" dir="ltr" title={record}>{record}</span>
      : <span className="text-muted-foreground">·</span>;
  };

  /** Every recorded parameter of the action, in reading order. `mono` marks identifiers and technical values. */
  const detailFields = (row: ActionTrailEvent): { key: string; value: string | null | undefined; mono?: boolean }[] => [
    { key: 'actionTrail.when', value: formatWhen(row.occurredUtc) },
    { key: 'actionTrail.whenUtc', value: utcStamp(row.occurredUtc), mono: true },
    { key: 'actionTrail.areaLabel', value: areaLabel(row.area) },
    { key: 'actionTrail.action', value: humanise(row.action) },
    { key: 'actionTrail.kindLabel', value: kindLabel(row.kind) },
    { key: 'actionTrail.outcomeLabel', value: outcomeLabel(row.outcome) },
    { key: 'actionTrail.actor', value: actorLabel(row) },
    { key: 'actionTrail.roles', value: row.actorRoles },
    { key: 'actionTrail.subject', value: row.redacted ? t('actionTrail.redacted') : personLabel(row.targetKind, row.targetName, row.targetStaffNo) },
    { key: 'actionTrail.record', value: row.redacted ? null : recordText(row), mono: true },
    { key: 'actionTrail.technicalName', value: [row.area, row.action].filter(Boolean).join(' / '), mono: true },
    { key: 'actionTrail.request', value: [row.httpMethod, row.redacted ? row.routeTemplate : (maskGuids(row.path) || row.routeTemplate)].filter(Boolean).join(' '), mono: true },
    { key: 'actionTrail.statusCode', value: row.statusCode ? String(row.statusCode) : null, mono: true },
    { key: 'actionTrail.duration', value: t('actionTrail.durationMs', { count: row.durationMs ?? 0 }) },
    { key: 'actionTrail.source', value: row.ip, mono: true },
    { key: 'actionTrail.userAgent', value: row.userAgent, mono: true },
    { key: 'actionTrail.correlationId', value: row.correlationId, mono: true },
  ];

  const stateLabel = (state: string) => t(`actionTrail.state.${state}`, { defaultValue: state });

  const emptyValue = <span className="text-muted-foreground/70">{t('actionTrail.valueEmpty')}</span>;

  const valueCell = (value: string | null | undefined, emphasis: boolean) => {
    if (value === null || value === undefined || value === '') return emptyValue;
    // The server already names a user id of this company; a GUID left over is an internal key and is never shown
    // (owner rule 2026-10-28).
    // "{id}" is the server's own mask for such a key (CO-953 (6)); an older server sends the GUID itself.
    if (isGuid(value) || value.trim() === '{id}') return <span className="text-muted-foreground">{t('actionTrail.valueInternalRef')}</span>;
    const shown = value === 'true' ? t('actionTrail.valueYes') : value === 'false' ? t('actionTrail.valueNo') : maskGuids(value);
    return (
      <span className={'whitespace-pre-wrap break-words ' + (emphasis ? 'font-medium text-foreground' : 'text-muted-foreground')} dir="auto">
        {shown}
      </span>
    );
  };

  const fieldRow = (field: ActionTrailFieldChange, state: string) => {
    const masked = field.secret || field.hidden;
    return (
      <tr key={field.name} className="border-t border-border align-top" data-testid="action-trail-field" data-field={field.name}>
        <th scope="row" className="py-1.5 pe-3 text-start font-normal text-muted-foreground">
          <span className="break-words">{humanise(field.name)}</span>
        </th>
        {masked ? (
          <td colSpan={2} className="py-1.5 text-muted-foreground" data-testid={field.secret ? 'action-trail-field-secret' : 'action-trail-field-hidden'}>
            <span className="inline-flex items-center gap-1.5">
              {field.secret
                ? <KeyRound className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                : <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
              {field.secret ? t('actionTrail.valueSecret') : t('actionTrail.valueHidden')}
            </span>
          </td>
        ) : (
          <>
            <td className="py-1.5 pe-3" data-testid="action-trail-before">
              {state === 'Added' ? emptyValue : valueCell(field.before, false)}
            </td>
            <td className="py-1.5" data-testid="action-trail-after">
              {/* A row the interceptor saw deleted never carries an After value, so this shows Empty for it.
                  A hand-written Deleted summary (CO-922 TournamentCascadeDelete) records its counts as After. */}
              {valueCell(field.after, true)}
            </td>
          </>
        )}
      </tr>
    );
  };

  // A composite key can carry a user id ("<user id>, [AspNetUserStore], AuthenticatorKey", "<user id>, <role id>"):
  // a bare GUID key is dropped, and GUID parts of a composite one are masked. The CLR generic arity ("`1") is noise.
  const changeKey = (key: string | null | undefined) => (withoutGuid(key) ? maskGuids(key) : null);
  const changeBlock = (change: ActionTrailChange, index: number) => (
    <div key={`${change.entity}-${change.key ?? ''}-${index}`} className="min-w-0" data-testid="action-trail-change" data-entity={change.entity} data-state={change.state}>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-xs font-semibold text-foreground">{humanise(change.entity.replace(/`\d+$/, ''))}</span>
        {changeKey(change.key) && <span className="font-mono text-[11px] text-muted-foreground" dir="ltr">#{changeKey(change.key)}</span>}
        <span
          className={
            'inline-flex items-center rounded border px-1.5 py-px text-[11px] font-medium leading-4 ' +
            (change.state === 'Deleted'
              ? 'border-red-200 text-red-700 dark:border-red-900/60 dark:text-red-300'
              : 'border-border text-muted-foreground')
          }
        >
          {stateLabel(change.state)}
        </span>
      </div>
      {change.fields.length > 0 ? (
        <table className="mt-1.5 w-full table-fixed text-xs">
          <colgroup>
            <col className="w-[30%]" />
            <col className="w-[35%]" />
            <col />
          </colgroup>
          <thead>
            <tr className="text-[11px] text-muted-foreground">
              <th scope="col" className="pb-1 pe-3 text-start font-medium">{t('actionTrail.field')}</th>
              <th scope="col" className="pb-1 pe-3 text-start font-medium">{t('actionTrail.before')}</th>
              <th scope="col" className="pb-1 text-start font-medium">{t('actionTrail.after')}</th>
            </tr>
          </thead>
          <tbody>{change.fields.map((f) => fieldRow(f, change.state))}</tbody>
        </table>
      ) : (
        <p className="mt-1 text-xs text-muted-foreground">{t('actionTrail.noFieldValues')}</p>
      )}
      {(change.omittedFields ?? 0) > 0 && (
        <p className="mt-1 text-[11px] text-muted-foreground">{t('actionTrail.omittedFields', { count: change.omittedFields })}</p>
      )}
    </div>
  );

  const detailsPanel = (row: ActionTrailEvent, actionText: string) => {
    const changes = row.changes ?? [];
    return (
      <div className="sticky start-0 space-y-5 py-2" style={{ maxWidth: panelMaxWidth }} data-testid="action-trail-details">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-foreground" data-testid="action-trail-details-title">{actionText}</p>
            {kindChip(row.kind)}
            {outcomeBadge(row.outcome)}
          </div>
          <p className="text-xs text-muted-foreground tabular-nums">
            {formatWhen(row.occurredUtc)}
            <span aria-hidden="true"> · </span>
            {actorLabel(row)}
          </p>
          {row.redacted && (
            <p className="flex items-center gap-1.5 pt-1 text-xs text-muted-foreground">
              <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {t('actionTrail.redactedHint')}
            </p>
          )}
        </div>

        {!row.redacted && (
          <section aria-label={t('actionTrail.changes')} data-testid="action-trail-changes">
            <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t('actionTrail.changes')}</h4>
            {changes.length > 0 ? (
              <div className="grid grid-cols-1 gap-x-8 gap-y-5 xl:grid-cols-2">{changes.map(changeBlock)}</div>
            ) : (
              <p className="text-xs text-muted-foreground" data-testid="action-trail-no-changes">{t('actionTrail.noChanges')}</p>
            )}
            {(row.omittedChanges ?? 0) > 0 && (
              <p className="mt-2 text-[11px] text-muted-foreground">{t('actionTrail.omittedRecords', { count: row.omittedChanges })}</p>
            )}
          </section>
        )}

        <section aria-label={t('actionTrail.allDetails')}>
          <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t('actionTrail.allDetails')}</h4>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2.5 text-xs sm:grid-cols-2 lg:grid-cols-3" data-testid="action-trail-params">
            {detailFields(row).filter((f) => f.value).map((f) => (
              <div key={f.key} className="min-w-0" data-testid="action-trail-param" data-param={f.key}>
                <dt className="text-muted-foreground">{t(f.key)}</dt>
                {f.mono
                  ? <dd className="break-all font-mono text-foreground" dir="ltr">{f.value}</dd>
                  : <dd className="break-words text-foreground" dir="auto">{f.value}</dd>}
              </div>
            ))}
          </dl>
        </section>
      </div>
    );
  };

  const filtered = hasAnyFilter(applied);

  return (
    <PageLayout
      title={t('Action Trail')}
      description={t('actionTrail.scope')}
      icon={History}
      accentColor="slate"
      headerActions={
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          disabled={exporting || loading || rows.length === 0}
          data-testid="action-trail-export"
        >
          <Download className="h-4 w-4 me-2" aria-hidden="true" />
          {t('actionTrail.exportPage')}
        </Button>
      }
    >
      <div className="mx-4 sm:mx-6 my-4 space-y-4" data-testid="action-trail-page">
        <form
          className="rounded-xl border border-border bg-card p-4"
          onSubmit={(e) => { e.preventDefault(); applyFilters(); }}
          aria-label={t('actionTrail.filters')}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <DateField
              label={t('actionTrail.from')}
              value={draft.from}
              max={draft.to || undefined}
              onChange={(v) => setDraft({ ...draft, from: v })}
              data-testid="action-trail-from"
            />
            <DateField
              label={t('actionTrail.to')}
              value={draft.to}
              min={draft.from || undefined}
              onChange={(v) => setDraft({ ...draft, to: v })}
              data-testid="action-trail-to"
            />
            <Select
              label={t('actionTrail.areaLabel')}
              options={areaOptions}
              value={draft.area}
              searchable
              onChange={(v: string | number) => setDraft({ ...draft, area: String(v ?? '') })}
              data-testid="action-trail-area"
            />
            <Select
              label={t('actionTrail.kindLabel')}
              options={kindOptions}
              value={draft.kind}
              onChange={(v: string | number) => setDraft({ ...draft, kind: String(v ?? '') })}
              data-testid="action-trail-kind"
            />
            <Select
              label={t('actionTrail.outcomeLabel')}
              options={outcomeOptions}
              value={draft.outcome}
              onChange={(v: string | number) => setDraft({ ...draft, outcome: String(v ?? '') })}
              data-testid="action-trail-outcome"
            />
            <Input
              label={t('actionTrail.actor')}
              placeholder={t('actionTrail.userPlaceholder')}
              value={draft.actor}
              onChange={(e) => setDraft({ ...draft, actor: e.target.value })}
              data-testid="action-trail-actor"
            />
            <Input
              label={t('actionTrail.subject')}
              placeholder={t('actionTrail.userPlaceholder')}
              value={draft.targetUser}
              onChange={(e) => setDraft({ ...draft, targetUser: e.target.value })}
              data-testid="action-trail-target"
            />
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">{t('actionTrail.subjectFilterNote')}</p>
            <div className="flex flex-wrap items-center gap-2 ms-auto">
              <Button type="button" variant="ghost" size="sm" onClick={resetFilters}>
                {t('actionTrail.reset')}
              </Button>
              <Button type="submit" size="sm" data-testid="action-trail-apply">
                {t('actionTrail.apply')}
              </Button>
            </div>
          </div>
        </form>

        <div ref={cardRef} className="rounded-xl border border-border bg-card">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
            <p className="text-sm text-muted-foreground tabular-nums" aria-live="polite">
              {t('actionTrail.count', { count: totalCount })}
            </p>
            <p className="text-xs text-muted-foreground">{t('actionTrail.retention')}</p>
          </div>

          {loading ? (
            <div className="divide-y divide-border" aria-busy="true" aria-label={t('actionTrail.loading')}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-4 py-3">
                  <Skeleton className="h-4 w-4" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="hidden h-4 w-28 sm:block" />
                  <Skeleton className="hidden h-4 w-36 md:block" />
                </div>
              ))}
            </div>
          ) : loadError ? (
            <div className="px-4 py-12 text-center" role="alert">
              <p className="text-sm text-foreground">{t('actionTrail.loadFailed')}</p>
              <Button className="mt-3" variant="outline" size="sm" onClick={() => void load()}>
                {t('Retry')}
              </Button>
            </div>
          ) : rows.length === 0 ? (
            <div className="px-4 py-12 text-center" data-testid="action-trail-empty">
              <p className="text-sm text-foreground">
                {filtered ? t('actionTrail.empty') : t('actionTrail.emptyNothingYet')}
              </p>
              <p className="mx-auto mt-1 max-w-[60ch] text-xs text-muted-foreground">
                {filtered ? t('actionTrail.emptyFilteredHint') : t('actionTrail.emptyNothingYetHint')}
              </p>
              {filtered && (
                <Button className="mt-3" variant="outline" size="sm" onClick={resetFilters}>
                  {t('actionTrail.reset')}
                </Button>
              )}
            </div>
          ) : (
            <Table data-testid="action-trail-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"><span className="sr-only">{t('actionTrail.details')}</span></TableHead>
                  <TableHead>{t('actionTrail.when')}</TableHead>
                  <TableHead>{t('actionTrail.action')}</TableHead>
                  <TableHead>{t('actionTrail.outcomeLabel')}</TableHead>
                  <TableHead>{t('actionTrail.actor')}</TableHead>
                  <TableHead>{t('actionTrail.subject')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const open = expandedId === row.id;
                  const actionText = `${areaLabel(row.area)} · ${humanise(row.action)}`;
                  return (
                    <Fragment key={row.id}>
                      <TableRow
                        data-testid="action-trail-row"
                        data-area={row.area}
                        data-action={row.action}
                        data-outcome={row.outcome}
                        data-redacted={row.redacted ? 'true' : 'false'}
                        data-changes={row.changes?.length ?? 0}
                        className={'cursor-pointer' + (open ? ' bg-muted/30' : '')}
                        onClick={() => setExpandedId(open ? null : row.id)}
                      >
                        <TableCell>
                          <button
                            type="button"
                            className="cursor-pointer rounded p-1 text-muted-foreground transition-colors duration-150 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                            aria-expanded={open}
                            aria-label={t('actionTrail.details')}
                            data-testid="action-trail-expand"
                            onClick={(e) => { e.stopPropagation(); setExpandedId(open ? null : row.id); }}
                          >
                            {open
                              ? <ChevronDown className="h-4 w-4" aria-hidden="true" />
                              : <ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />}
                          </button>
                        </TableCell>
                        <TableCell className="whitespace-nowrap tabular-nums">{formatWhen(row.occurredUtc)}</TableCell>
                        <TableCell>
                          <span className="flex min-w-0 items-center gap-2">
                            <span className="max-w-[15rem] truncate" title={actionText} data-testid="action-trail-action">{actionText}</span>
                            {kindChip(row.kind)}
                          </span>
                        </TableCell>
                        <TableCell>{outcomeBadge(row.outcome)}</TableCell>
                        <TableCell data-testid="action-trail-actor">{actorCell(row)}</TableCell>
                        <TableCell data-testid="action-trail-subject">{subjectCell(row)}</TableCell>
                      </TableRow>
                      {open && (
                        <TableRow className="bg-muted/30 hover:bg-muted/30">
                          <TableCell colSpan={COLUMN_COUNT} className="px-4 sm:px-6">
                            {detailsPanel(row, actionText)}
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {totalPages > 1 && (
            <div className="border-t border-border px-4 py-3">
              <Pagination
                currentPage={pageNumber}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={pageSize}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                onPageSizeChange={(size) => { setPageSize(size); setPageNumber(1); }}
                onPageChange={(p) => { setPageNumber(p); setExpandedId(null); }}
              />
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground">{t('actionTrail.notListed')}</p>
      </div>
    </PageLayout>
  );
};

export default ActionTrail;
